import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { cn } from "../lib/utils";
import { focusStyles, hoverStyles, disabledStyles } from "../utils/styles";
import ErrorBoundary from "./ErrorBoundary";
import { HeroSection } from "./preLaunch/PreLaunchContent";
import { SuccessMessage } from "./preLaunch/SuccessMessage";
import Card from "./shared/Card";
import { ApproachFlow } from "./preLaunch/ApproachFlow";
import ContactDialog from "./dialogs/ContactDialog";
import { Spinner } from "../components/ui/spinner";
import {
  PRE_LAUNCH_CONTENT,
  LOGO_IMAGES,
  EMAIL_REGEX,
  EMAIL_ERROR_MESSAGE,
  FORMSUBMIT_EMAIL,
  FORMSUBMIT_SUBJECT_PRELAUNCH,
  TOAST_MESSAGES,
} from "../constants/preLaunch";

// Shared image styling constants
const IMAGE_BASE_CLASSES =
  "h-auto object-contain transition-transform duration-300 hover:scale-110 opacity-90 hover:opacity-100";

export function PreLaunchLandingPage() {
  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = useForm<{ email: string }>({
    defaultValues: { email: "" },
  });

  const onSubmit = async (data: { email: string }) => {
    try {
      const response = await fetch(`https://formsubmit.co/ajax/${FORMSUBMIT_EMAIL}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          email: data.email,
          _subject: FORMSUBMIT_SUBJECT_PRELAUNCH,
          _captcha: "false",
        }),
      });

      if (!response.ok) {
        toast.error(TOAST_MESSAGES.prelaunch.error);
      }
      // On success, form will show SuccessMessage component via isSubmitSuccessful
    } catch (error) {
      toast.error(TOAST_MESSAGES.prelaunch.error);
    }
  };

  return (
    <ErrorBoundary>
      {/* Skip to main content link for screen readers */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-brand-primary focus:text-text-primary focus:rounded-lg focus:font-bold"
      >
        Skip to main content
      </a>

      <main
        id="main-content"
        className={cn(
          "landing-page-bg",
          "min-h-screen flex flex-col relative",
          "px-4 py-12",
          "font-sans",
          "overflow-x-hidden max-w-full"
        )}
        role="main"
        aria-label="Find a Feeling - Pre-Launch"
      >
        {/* Mobile left logo - above hero */}
        <div className="md:hidden flex justify-center mb-6 max-w-full overflow-hidden">
          <img
            src={LOGO_IMAGES[0].src}
            alt={LOGO_IMAGES[0].alt}
            className={cn("w-[26.25vw] max-w-[105px]", IMAGE_BASE_CLASSES)}
            style={{ transform: "rotate(15deg)" }}
          />
        </div>

        <div className="flex-1 flex items-stretch justify-center gap-2 md:gap-4 lg:gap-6 xl:gap-8 max-w-full">
          {/* Left side logo */}
          <div className="hidden md:flex flex-col items-center justify-start flex-1 md:w-[20vw] lg:w-[25vw] self-stretch px-4 ml-8">
            <img
              src={LOGO_IMAGES[0].src}
              alt={LOGO_IMAGES[0].alt}
              className={cn("w-[85%] max-h-[60vh]", IMAGE_BASE_CLASSES)}
              style={{ transform: "rotate(15deg)" }}
            />
          </div>

          {/* Center content - 60% on tablet+ */}
          <div className="flex-shrink-0 w-full md:w-[60vw] self-center">
            {/* Title and Subtitle - Matches landing page */}
            <HeroSection
              title={PRE_LAUNCH_CONTENT.title}
              subtitle={PRE_LAUNCH_CONTENT.subtitle}
              containerClassName="mb-8 sm:mb-10"
            />

            {/* Email Form or Success Message */}
            {!isSubmitSuccessful ? (
              <form onSubmit={handleSubmit(onSubmit)} className="max-w-4xl mx-auto" noValidate>
                {/* Info Cards - Grid Layout */}
                <section
                  className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-8 sm:mb-10"
                  aria-label="App information and survey"
                >
                  {/* What & Who Card - Combined */}
                  <Card className="border-border-default bg-surface-alt">
                    <div className="space-y-6 text-center">
                      <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-brand-accent-mint font-sans mb-4 text-center">
                        What is it?
                      </h2>
                      <p className="text-sm sm:text-base md:text-lg text-text-primary font-sans leading-relaxed text-start">
                        {PRE_LAUNCH_CONTENT.whatAndWhy.description}
                      </p>
                      <div className="pt-2 space-y-2">
                        <p className="text-sm sm:text-base md:text-lg text-text-primary font-sans text-start">
                          {PRE_LAUNCH_CONTENT.whatAndWhy.problem}
                        </p>
                        <p className="text-sm sm:text-base md:text-lg text-brand-secondary font-sans font-bold">
                          {PRE_LAUNCH_CONTENT.whatAndWhy.solution}
                        </p>
                      </div>

                      {/* Who section */}
                      <div className="pt-4 border-t border-border-default">
                        <h4 className="text-base sm:text-lg md:text-xl font-bold text-brand-accent-mint font-sans mb-2">
                          {PRE_LAUNCH_CONTENT.audience.heading}
                        </h4>
                        <p className="text-sm sm:text-base md:text-lg text-text-primary font-sans">
                          {PRE_LAUNCH_CONTENT.audience.groups.join(" • ")}
                        </p>
                      </div>
                    </div>
                  </Card>

                  {/* Survey Card */}
                  <Card className="border-border-default bg-surface-alt">
                    <div className="space-y-6 text-center">
                      <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-brand-accent-mint font-sans mb-4 text-center">
                        {PRE_LAUNCH_CONTENT.survey.heading}
                      </h2>
                      <div className="space-y-4">
                        <p className="text-sm sm:text-base md:text-lg text-text-primary font-sans leading-relaxed text-start">
                          {PRE_LAUNCH_CONTENT.survey.description}
                        </p>
                        <p className="text-sm sm:text-base text-text-primary font-sans text-start">
                          {PRE_LAUNCH_CONTENT.survey.benefits}
                        </p>
                      </div>

                      <div className="space-y-4">
                        <a
                          href={PRE_LAUNCH_CONTENT.survey.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={cn(
                            "inline-block px-6 py-3 text-base sm:text-lg font-bold rounded-full",
                            "bg-brand-accent-mint text-surface-default border-2 border-brand-accent-mint",
                            "hover:bg-brand-accent-mint/80 hover:text-surface-default",
                            "transition-all duration-200",
                            focusStyles.ring,
                            hoverStyles.scale
                          )}
                          aria-label="Open survey in new tab"
                        >
                          {PRE_LAUNCH_CONTENT.survey.buttonText}
                        </a>
                        <p className="text-sm sm:text-base text-text-primary font-sans italic">
                          {PRE_LAUNCH_CONTENT.survey.thankYou}
                        </p>
                      </div>
                    </div>
                  </Card>
                </section>

                {/* Approach Flow - How it works */}
                <ApproachFlow
                  steps={PRE_LAUNCH_CONTENT.approach.steps}
                  arrowColor={PRE_LAUNCH_CONTENT.approach.arrowColor}
                />

                {/* CTA Section */}
                <div className="mb-8 sm:mb-12 text-center space-y-4" style={{ paddingTop: "3em" }}>
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-brand-secondary font-sans">
                    {PRE_LAUNCH_CONTENT.cta.heading}
                  </h3>
                  <p className="text-base sm:text-lg md:text-xl text-text-primary font-sans">
                    {PRE_LAUNCH_CONTENT.cta.description}
                  </p>
                </div>

                <div className="flex flex-col lg:flex-row gap-3 lg:gap-4">
                  {/* Email Input */}
                  <input
                    type="email"
                    {...register("email", {
                      required: EMAIL_ERROR_MESSAGE,
                      pattern: {
                        value: EMAIL_REGEX,
                        message: EMAIL_ERROR_MESSAGE,
                      },
                    })}
                    placeholder={PRE_LAUNCH_CONTENT.cta.placeholder}
                    disabled={isSubmitting}
                    className={cn(
                      "flex-1 px-6 py-4 text-base sm:text-lg rounded-full font-sans",
                      "bg-white/95 text-surface-default placeholder:text-gray-500",
                      "focus:outline-none focus:ring-2 transition-all duration-200",
                      disabledStyles.default,
                      errors.email
                        ? "border-2 border-state-error focus:ring-state-error focus:border-state-error"
                        : "border-2 border-white/20 focus:ring-brand-accent-mint focus:border-brand-accent-mint"
                    )}
                    aria-label="Email address"
                    aria-invalid={errors.email ? "true" : "false"}
                    aria-describedby={errors.email ? "email-error" : undefined}
                  />

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={cn(
                      "min-h-[2rem] sm:min-h-[2.5rem] px-4 sm:px-8 py-1 sm:py-2",
                      "text-base sm:text-xl font-bold rounded-full whitespace-nowrap",
                      "bg-brand-primary text-text-primary font-sans",
                      "border-2 border-brand-primary hover:border-brand-secondary",
                      "hover:bg-brand-primary/80 hover:text-text-primary",
                      "inline-flex items-center justify-center gap-2",
                      hoverStyles.scale,
                      focusStyles.ring,
                      disabledStyles.default
                    )}
                    aria-label="Submit email to be notified"
                  >
                    {isSubmitting && (
                      <Spinner variant="circle" size={20} className="text-text-primary" />
                    )}
                    {isSubmitting
                      ? PRE_LAUNCH_CONTENT.cta.submittingText
                      : PRE_LAUNCH_CONTENT.cta.buttonText}
                  </button>
                </div>

                {/* Error message */}
                {errors.email && (
                  <div
                    id="email-error"
                    role="alert"
                    aria-live="assertive"
                    className="mt-2 ml-[1em] flex items-center gap-2"
                  >
                    <span className="text-state-error text-base font-bold" aria-hidden="true">
                      ⚠
                    </span>
                    <p className="text-sm sm:text-base text-state-error font-sans font-medium">
                      {errors.email.message}
                    </p>
                  </div>
                )}

                {/* Privacy note - from constants */}
                <p className="text-center text-base text-text-primary font-sans italic mt-4">
                  {PRE_LAUNCH_CONTENT.privacy}
                </p>
              </form>
            ) : (
              <SuccessMessage />
            )}
          </div>

          {/* Right side logos */}
          <div className="hidden md:flex flex-col items-end justify-end flex-1 md:w-[20vw] lg:w-[25vw] self-stretch pb-4 gap-8">
            <img
              src={LOGO_IMAGES[1].src}
              alt={LOGO_IMAGES[1].alt}
              className={cn("w-[70%] max-h-[35vh]", IMAGE_BASE_CLASSES)}
              style={{ transform: "rotate(-60deg)", marginLeft: "1em" }}
            />
            <img
              src={LOGO_IMAGES[2].src}
              alt={LOGO_IMAGES[2].alt}
              className={cn("w-[70%] max-h-[28vh]", IMAGE_BASE_CLASSES)}
              style={{ transform: "rotate(5deg)", marginLeft: "1em" }}
            />
          </div>
        </div>

        {/* Mobile logos - below hero, above footer */}
        <div className="md:hidden flex flex-row items-center justify-center gap-6 mt-8 mb-8 max-w-full pb-4">
          <img
            src={LOGO_IMAGES[1].src}
            alt={LOGO_IMAGES[1].alt}
            className={cn("w-[28vw] max-w-[110px]", IMAGE_BASE_CLASSES)}
            style={{ transform: "rotate(-45deg)" }}
          />
          <img
            src={LOGO_IMAGES[2].src}
            alt={LOGO_IMAGES[2].alt}
            className={cn("w-[24vw] max-w-[90px]", IMAGE_BASE_CLASSES)}
            style={{ transform: "rotate(5deg)" }}
          />
        </div>

        {/* Footer Contact - Bottom of page */}
        <footer className="text-center mt-auto" style={{ paddingTop: "3em" }}>
          <p className="text-base sm:text-lg text-text-primary font-sans">
            <button
              onClick={() => setIsContactDialogOpen(true)}
              className={cn(
                "text-brand-accent-mint hover:text-brand-accent-mint/80",
                "underline transition-colors font-medium",
                "bg-transparent border-none cursor-pointer",
                focusStyles.ring
              )}
              aria-label={PRE_LAUNCH_CONTENT.footer.contactDescription}
            >
              {PRE_LAUNCH_CONTENT.footer.linkText}
            </button>{" "}
            — {PRE_LAUNCH_CONTENT.footer.contactDescription} or email{" "}
            <a
              href={`mailto:${PRE_LAUNCH_CONTENT.footer.email}`}
              className={cn(
                "text-brand-accent-mint hover:text-brand-accent-mint/80",
                "underline transition-colors font-medium",
                focusStyles.ring
              )}
            >
              {PRE_LAUNCH_CONTENT.footer.email}
            </a>
          </p>
        </footer>
      </main>
      <ToastContainer
        theme="dark"
        position="bottom-right"
        toastStyle={{
          backgroundColor: "#1a1a1a",
          color: "#ffffff",
        }}
        style={
          {
            "--toastify-color-success": "#4ade80",
            "--toastify-text-color-success": "#ffffff",
          } as React.CSSProperties
        }
      />
      <ContactDialog
        isOpen={isContactDialogOpen}
        onClose={() => setIsContactDialogOpen(false)}
        isLightMode={false}
      />
    </ErrorBoundary>
  );
}

export default PreLaunchLandingPage;
